import { Button } from "@/components/ui/button";
import { ArrowRight, Phone, Award } from "lucide-react";
import { useState, useEffect } from "react";
import heroImage1 from "@/assets/hero-1.webp";
import heroImage2 from "@/assets/hero-2.webp";
import heroImage3 from "@/assets/hero-3.webp";
import heroImage4 from "@/assets/hero-4.webp";
import heroImage5 from "@/assets/hero-5.webp";
import heroImage6 from "@/assets/hero-6.webp";
import heroImage7 from "@/assets/hero-7.webp";

const heroImages = [
  { src: heroImage1, alt: "Fassadensanierung Herford – Malerfachbetrieb Stein" },
  { src: heroImage2, alt: "Professionelle Malerarbeiten in Herford" },
  { src: heroImage3, alt: "Innenarbeiten und Wandgestaltung Herford" },
  { src: heroImage4, alt: "Fugenlose Badsanierung Herford" },
  { src: heroImage5, alt: "Exklusive Oberflächen und Spachteltechnik" },
  { src: heroImage6, alt: "Fassadengestaltung und WDVS Herford" },
  { src: heroImage7, alt: "Mehrfamilienhaus Sanierung Herford" },
];

export const Hero = () => {
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % heroImages.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  return (
    <section className="relative min-h-[88vh] md:min-h-[78vh] flex items-end overflow-hidden pt-32 md:pt-24 pb-12 md:pb-20">
      {/* Full-Screen Background Images with Crossfade */}
      <div className="absolute inset-0">
        {heroImages.map((image, index) => (
          <div
            key={index}
            className="absolute inset-0 transition-opacity duration-1000 ease-in-out"
            style={{ opacity: currentIndex === index ? 1 : 0 }}
          >
            <img 
              src={image.src} 
              alt={image.alt}
              className="w-full h-full object-cover"
              width={1920}
              height={1080}
              {...(index === 0
                ? { fetchPriority: "high" as const, loading: "eager" as const, decoding: "async" as const }
                : { loading: "lazy" as const, decoding: "async" as const })}
            />
          </div>
        ))}
        <div className="absolute inset-0 bg-foreground/60" />
      </div>
      
      <div className="relative z-10 container mx-auto">
        {/* Main Heading – SEO-optimized H1 */}
        <div className="max-w-4xl">
          <div className="inline-flex items-center gap-2 mb-5 md:mb-6 text-[10px] tracking-[0.25em] uppercase text-background/70">
            <span className="h-px w-8 bg-background/40" />
            Meistergeführter Fachbetrieb seit 2014
          </div>

          <h1 className="font-caslon text-[26px] sm:text-3xl md:text-5xl lg:text-6xl font-bold mb-5 md:mb-6 tracking-tight text-background leading-[1.1]">
            Malerfachbetrieb Stein:
            <br />
            <span className="text-background/80">Energetische Sanierung</span>
            <br />
            & WDVS in Herford
          </h1>

          {/* SEO Subline */}
          <p className="text-sm md:text-lg mb-8 md:mb-10 max-w-2xl text-background/70 font-light leading-relaxed">
            Wir schützen Ihre Bausubstanz, stoppen Schimmel und senken Ihre Heizkosten nachhaltig. 
            Meistergeführte Fassadendämmung und Innensanierung aus einer Hand.
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 w-full">
            <Button 
              size="lg" 
              className="bg-background text-foreground hover:bg-background/90 font-medium px-6 sm:px-8 py-6 text-xs sm:text-sm rounded-lg transition-all duration-300 hover:translate-y-[-2px] hover:shadow-xl group"
              onClick={() => document.getElementById('kontakt')?.scrollIntoView({ behavior: 'smooth' })}
            >
              Kostenlosen Fassaden-Check anfordern
              <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
            </Button>
            <a href="tel:+4952213832130">
              <Button 
                size="lg" 
                variant="outline"
                className="w-full sm:w-auto bg-transparent border border-background/40 text-background hover:bg-background hover:text-foreground hover:border-background font-medium px-6 sm:px-8 py-6 text-xs sm:text-sm rounded-lg transition-all duration-300"
              >
                <Phone className="mr-2 h-5 w-5" />
                05221 / 3832130
              </Button>
            </a>
          </div>
        </div>
      </div>

      {/* Bottom Gradient Fade */}
      <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-background to-transparent" />
    </section>
  );
};
